import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, Dialog, Stack, Typography } from '@mui/material'
import MosSmartDialog from '../../../../common/MosSmartDialog'
import createDialogTitle from '../../../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import AlertUI from '../../../../../../generic/AlertUI'
import useMosInfo from '../../../../common/provider/useMosInfo'
import { isBlockedByIncidence, isBlockedBySingleJobMode, SINGLE_JOB_MODE_BLOCK_KEY } from '../../../../common/canStartJob'
import SingleJobModeBlockedSnackbar from '../../../../common/SingleJobModeBlockedSnackbar'
import useDocumentAuthorities from '../../../../common/documents/useDocumentAuthorities'
import { hasAnyDocumentViewPermission } from '../../../../common/documents/fabricationDocumentSources'
import FabricationDocumentsPanel from '../../../../common/documents/FabricationDocumentsPanel'
import JobStatusChangeDialog from '../JobStatusChangeDialog'
import useJobDetail, { BLOCK_STATUS, FIRST_LOAD } from '../../hooks/useJobDetail'
import { changeDialogInfo } from '../../utils/changeDialogInfo'
import { selectsJobOnSuccess } from '../../utils/jobStatus'
import { JOB_DETAIL_TAB, visibleTabs as resolveVisibleTabs } from '../../utils/jobDetailTabs'
import JobDetailHeader from './JobDetailHeader'
import JobDetailStatusActions from './JobDetailStatusActions'
import JobDetailQrPopover from './JobDetailQrPopover'
import JobDetailKpiStrip from './JobDetailKpiStrip'
import JobSequenceStrip from './JobSequenceStrip'
import JobDetailBlockNav, { TAB_LABEL_KEY } from './JobDetailBlockNav'
import GeneralTab from './tabs/GeneralTab'
import MaterialsTab from './tabs/MaterialsTab'
import InspectionsTab from './tabs/InspectionsTab'
import IncidencesTab from './tabs/IncidencesTab'
import PartsTab from './tabs/PartsTab'

// Historia 160 (MIC-4303) — EL DETALLE DEL JOB. Lo montan los tres puntos de entrada (lupa de Planned
// Jobs, y "Show all" de Planned Jobs y de Operation) con la MISMA firma de siempre
// (`open, fabricationOrder, initialJob, setOpen`), así que ninguno cambia (RN2).
//
// Ocupa el ancho de la pantalla (no un modal "lg"): cabecera, tira de KPIs, secuencia y, debajo, la
// lista "Bloques" con la pestaña activa (mocks 1-7). A 1920×1080 cabe sin desplazar; por debajo, el
// detalle entero se desplaza en vertical y nada se recorta (RN47).
//
// ⚠️ RN46/R-D2 — EL DIÁLOGO DE CAMBIO DE ESTADO NO VIVE DENTRO DE LO QUE CAMBIA CON D1. Se monta
// aquí, fuera de la cabecera, y recibe una FOTO del job tomada al pulsar la acción: un tick que trae
// otro estado repinta la cabecera de fondo, pero no desmonta ni altera el diálogo abierto (S126).
//
// ⚠️ RN23 — tras un Start con éxito (arrancar, reanudar, rehacer, Join) el job queda seleccionado en
// el puesto con la misma resolución que la fila (`changeDialogInfo` + `selectNode`) y el detalle se
// cierra; tras Interrupt/Finish sigue abierto y se recarga en el acto con los jobs activos del puesto.

const CLOSED_STATUS_DIALOG = {
  open: false,
  node: {},
  fabricationOrder: {},
  jobReference: {},
  statusCode: '',
  intent: '',
  managePreparation: false
}

const JobDetailMos = ({ open, fabricationOrder, initialJob, setOpen }) => {
  const { t_ } = useSmartTranslation()
  const { device, globalData, activeJobs, selectNode, refreshActiveJobs } = useMosInfo()
  const isUserAuthorized = useDocumentAuthorities()
  const canViewDocuments = hasAnyDocumentViewPermission(isUserAuthorized)
  const canGenerateQr = isUserAuthorized('GENERATE_QR_CODE')
  const [AlertElement, showAlert] = AlertUI()
  const [statusDialog, setStatusDialog] = useState(CLOSED_STATUS_DIALOG)
  const fabricationOrderId = fabricationOrder?.id ?? ''

  // E5/RN53/G11: aviso no bloqueante con el snackbar estándar de MOS; los datos se conservan.
  const handleRefreshError = useCallback(() => {
    showAlert({ severity: 'warning', title: t_('Could not refresh the job detail'), message: ' ', duration: 5000 })
  }, [showAlert, t_])

  // CU2 A2 / RN7: el literal de Sun, traducido; el detalle sigue abierto.
  const handleNavigationError = useCallback((literal) => {
    showAlert({ severity: 'error', message: literal ? t_(literal) : '', duration: 5000 })
  }, [showAlert, t_])

  const {
    firstLoad,
    firstError,
    detail,
    selectedJobId,
    switching,
    activeTab,
    blocks,
    selectedIncidenceUuid,
    selectedPartId,
    footprint,
    selectJob,
    selectTab,
    retryBlock,
    selectIncidence,
    selectPart,
    reloadNow
  } = useJobDetail({
    open,
    fabricationOrderId,
    initialJob: initialJob ?? '',
    deviceId: device?.id ?? '',
    canViewDocuments,
    onRefreshError: handleRefreshError,
    onNavigationError: handleNavigationError
  })

  const handleClose = useCallback(() => {
    setStatusDialog(CLOSED_STATUS_DIALOG)
    setOpen(false)
  }, [setOpen])

  const job = useMemo(() => detail.job || {}, [detail.job])
  const tabs = useMemo(() => resolveVisibleTabs(detail.summary, canViewDocuments), [detail.summary, canViewDocuments])
  // Single Job Mode: the SAME rule the Planned Jobs row applies (`isBlockedBySingleJobMode`), fed the
  // job the detail shows. '' when nothing blocks, so the header keeps today's actions untouched.
  const startBlockedTooltipKey = isBlockedBySingleJobMode({
    globalData,
    activeJobs,
    fabricationOrderId,
    jobReferenceId: job.jobReferenceId
  })
    ? SINGLE_JOB_MODE_BLOCK_KEY
    : ''
  // ...and the snackbar the header shows when the operator presses the withheld Start/Join.
  const [startBlockedOpen, setStartBlockedOpen] = useState(false)
  const handleStartBlocked = useCallback(() => { setStartBlockedOpen(true) }, [])
  const closeStartBlocked = useCallback(() => { setStartBlockedOpen(false) }, [])

  // La FOTO del job al pulsar (RN46): el diálogo trabaja con ella aunque lleguen ticks.
  const handleAction = useCallback((action) => {
    setStatusDialog({
      open: true,
      node: job.node || {},
      fabricationOrder: fabricationOrder || {},
      jobReference: { id: job.jobReferenceId },
      statusCode: action.statusCode,
      intent: action.intent,
      managePreparation: job.managePreparation === true
    })
  }, [job, fabricationOrder])

  const handleStatusCancel = useCallback(() => setStatusDialog(CLOSED_STATUS_DIALOG), [])

  const handleStatusSuccess = useCallback(async () => {
    const snapshot = statusDialog
    setStatusDialog(CLOSED_STATUS_DIALOG)
    if (selectsJobOnSuccess(snapshot.intent)) {
      const { node, fabricationOrderInfo, jobReference } = await changeDialogInfo(snapshot)
      selectNode(node, fabricationOrderInfo, jobReference)
      setOpen(false)
      return
    }
    reloadNow()
    if (refreshActiveJobs) refreshActiveJobs()
  }, [statusDialog, selectNode, setOpen, reloadNow, refreshActiveJobs])

  const handleDialogClose = (event, reason) => {
    // Pulsar fuera no cierra el detalle; Escape sí (S21), y MUI solo lo entrega al diálogo de arriba.
    if (reason === 'backdropClick') return
    handleClose()
  }

  if (firstLoad === FIRST_LOAD.FAILED) {
    // D12/S19: la primera carga fallida es un diálogo de error, como hoy; sin reloj.
    return (
      <MosSmartDialog
        setOpen={open}
        closeCallback={handleClose}
        title={createDialogTitle({ color: 'error', title: t_('Error'), icon: null })}
      >
        <Typography color='error' data-testid='job-detail-first-error'>{firstError ? t_(firstError) : ''}</Typography>
      </MosSmartDialog>
    )
  }

  const renderTab = () => {
    const blockOf = (tab) => {
      const block = blocks[tab]
      // Datos de otro job solo se enseñan (atenuados) mientras se cambia de job (CU2 paso 4).
      return block.jobReferenceId === selectedJobId || switching ? block : { ...block, status: BLOCK_STATUS.LOADING }
    }
    switch (activeTab) {
      case JOB_DETAIL_TAB.DOCUMENTS:
        return (
          <FabricationDocumentsPanel
            fabricationOrderId={fabricationOrderId}
            jobReferenceId={selectedJobId}
            requestToken={1}
            height='calc(100vh - 520px)'
          />
        )
      case JOB_DETAIL_TAB.MATERIALS:
        return <MaterialsTab block={blockOf(JOB_DETAIL_TAB.MATERIALS)} onRetry={retryBlock} />
      case JOB_DETAIL_TAB.INSPECTIONS:
        return <InspectionsTab block={blockOf(JOB_DETAIL_TAB.INSPECTIONS)} onRetry={retryBlock} />
      case JOB_DETAIL_TAB.INCIDENCES:
        return <IncidencesTab block={blockOf(JOB_DETAIL_TAB.INCIDENCES)} selectedUuid={selectedIncidenceUuid} onSelect={selectIncidence} onRetry={retryBlock} />
      case JOB_DETAIL_TAB.PARTS:
        return (
          <PartsTab
            block={blockOf(JOB_DETAIL_TAB.PARTS)}
            selectedPartId={selectedPartId}
            footprint={footprint}
            fabricationOrderId={fabricationOrderId}
            jobReferenceId={selectedJobId}
            onSelectPart={selectPart}
            onRetry={retryBlock}
          />
        )
      default:
        return <GeneralTab detail={detail} visibleTabs={tabs} onOpenTab={selectTab} />
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      fullWidth
      aria-label={t_('Job detail')}
      PaperProps={{
        'data-testid': 'job-detail',
        sx: {
          width: 'calc(100vw - 32px)',
          maxWidth: 'none',
          height: 'calc(100vh - 32px)',
          maxHeight: 'none',
          m: 2,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          overflow: 'hidden',
          position: 'relative'
        }
      }}
    >
      <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: (theme) => theme.zIndex.snackbar, minWidth: 420 }}>
        {AlertElement}
        <SingleJobModeBlockedSnackbar open={startBlockedOpen} onClose={closeStartBlocked} />
      </Box>
      {firstLoad === FIRST_LOAD.LOADING
        ? (
          <Stack alignItems='center' justifyContent='center' sx={{ flexGrow: 1 }} data-testid='job-detail-loading'>
            <CircularProgress aria-label={t_('Loading')} />
          </Stack>
          )
        : (
          <Box data-testid='job-detail-scroll' sx={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <JobDetailHeader
              detail={detail}
              onClose={handleClose}
              actions={<JobDetailStatusActions job={job} blocked={isBlockedByIncidence(globalData?.incidence)} startBlockedTooltipKey={startBlockedTooltipKey} onStartBlocked={handleStartBlocked} onAction={handleAction} />}
              qr={<JobDetailQrPopover fabricationOrder={detail.fabricationOrder} partReference={detail.partReference} canGenerate={canGenerateQr} />}
            />
            <JobDetailKpiStrip detail={detail} />
            <JobSequenceStrip
              sequence={detail.sequence || []}
              total={detail.quantities?.orderQuantity || 0}
              selectedJobId={selectedJobId}
              onSelect={selectJob}
            />
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ width: 320, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
                <JobDetailBlockNav tabs={tabs} activeTab={activeTab} openIncidences={detail.summary?.incidences?.open || 0} onSelect={selectTab} />
              </Box>
              <Box
                sx={{ flexGrow: 1, minWidth: 0, px: 3, py: 2, opacity: switching ? 0.55 : 1, transition: 'opacity 120ms' }}
                data-testid='job-detail-tab'
                data-switching={switching ? 'true' : 'false'}
                role='tabpanel'
              >
                <Typography component='h2' sx={{ fontSize: '1.45rem', fontWeight: 800, mb: 1.5 }}>{t_(TAB_LABEL_KEY[activeTab])}</Typography>
                {renderTab()}
              </Box>
            </Box>
          </Box>
          )}
      <JobStatusChangeDialog
        open={statusDialog.open}
        onCancell={handleStatusCancel}
        onSuccess={handleStatusSuccess}
        node={statusDialog.node}
        jobReference={statusDialog.jobReference}
        fabricationOrder={statusDialog.fabricationOrder}
        statusCode={statusDialog.statusCode}
        intent={statusDialog.intent}
        managePreparation={statusDialog.managePreparation}
      />
    </Dialog>
  )
}

JobDetailMos.propTypes = {
  open: PropTypes.bool.isRequired,
  fabricationOrder: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fabricationOrder: PropTypes.string,
    description: PropTypes.string,
    deliveryDate: PropTypes.string
  }),
  initialJob: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setOpen: PropTypes.func.isRequired
}

export default JobDetailMos
